<template>
    <a
        v-if="showPrice"
        v-intersect.once="onIntersect"
        :class="labelColor"
        class="conditions-label d-flex align-center cursor-pointer"
        @click.prevent="showConditions"
    >
        <v-icon size="small" start class="text-info">mdi-bell-alert-outline</v-icon>
        {{ label }}
    </a>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue} from 'vue-facing-decorator'
    import {EventBus, SHOW_CONDITIONS_MODAL_EVENT} from '@/utils/event-bus'

    // TODO Need refactoring with ConditionsMixin
    @Component
    export class ConditionsLabel extends Vue {
        @Prop() offerKey
        @Prop({required: true}) price
        @Prop({default: true, type: Boolean}) xSmall
        @Prop({default: false, type: Boolean}) small
        @Prop() offer

        @Emit()
        conditions() {}

        beforeUnmount() {
            if (runtimeStore.conditionsLoading[this.offerKey]) {
                runtimeStore.conditionsLoading[this.offerKey].cancel()
            }
        }

        async showConditions() {
            EventBus.$emit(SHOW_CONDITIONS_MODAL_EVENT, {
                offerKey: this.offerKey,
                conditionsInfo: null,
                offer: this.offer,
            })
            await this.$nextTick()
            this.conditions()
        }

        async onIntersect(isIntersecting) {
            if (
                isIntersecting &&
                !persistentStore.conditions[this.offerKey] &&
                !runtimeStore.conditionsLoading[this.offerKey]
            ) {
                await persistentStore.loadConditions({offerKey: this.offerKey})
            }
        }

        get isLoading() {
            return runtimeStore.isConditionsLoading(this.offerKey)
        }

        get hasErrors() {
            return !this.conditionsInfo.cancellationItems?.length
        }

        // TODO Same with hasErrors - wtf????
        get isFree() {
            return !this.conditionsInfo.cancellationItems.length
        }

        get isNonRefundable() {
            const firstCondition = this.conditionsInfo.cancellationItems[0]
            return (
                (firstCondition.dateFrom &&
                    firstCondition.price &&
                    new Date() >= this.$dateFns.parseISO(firstCondition.dateFrom) &&
                    this.$filters.convertPrice(this.price).amount <=
                        this.$filters.convertPrice(firstCondition.price).amount) ||
                this.offer?.rooms?.find(({groupedOffers}) =>
                    groupedOffers.map(({offerKey}) => offerKey).includes(this.offerKey)
                )?.nonRefundable ||
                this.offer?.offers?.find(({offerKey}) => offerKey === this.offerKey)?.nonRefundable
            )
        }

        get freeDate() {
            const firstCondition = this.conditionsInfo.cancellationItems[0]
            if (!firstCondition.dateFrom || new Date() >= this.$dateFns.parseISO(firstCondition.dateFrom)) {
                return undefined
            }
            return this.$dateFns.parse(firstCondition.dateFrom, 'yyyy-MM-dd HH:mm', new Date())
        }

        get feeForCancellation() {
            let result = undefined

            this.conditionsInfo.cancellationItems.map(cancellation => {
                const dateFrom = this.$dateFns.parseISO(cancellation.dateFrom)
                const dateTo = this.$dateFns.parseISO(cancellation.dateTo)

                if (
                    cancellation?.price?.amount < this.price.amount &&
                    (this.$dateFns.isAfter(new Date(), dateFrom) || this.$dateFns.isSameDay(new Date(), dateFrom)) &&
                    (this.$dateFns.isBefore(new Date(), dateTo) || this.$dateFns.isSameDay(new Date(), dateTo))
                )
                    result = this.$dateFns.parse(cancellation.dateFrom, 'yyyy-MM-dd HH:mm', new Date())
            })

            return result
        }

        get conditionsInfo() {
            const offerKey = this.offerKey
            return persistentStore.conditions[offerKey] || {cancellationItems: []}
        }

        get status() {
            if (this.isLoading) {
                return 'isLoading'
            } else if (this.hasErrors) {
                return 'hasErrors'
            } else if (this.isFree) {
                return 'isFree'
            } else if (this.isNonRefundable) {
                return 'isNonRefundable'
            } else if (this.freeDate) {
                return 'freeDate'
            } else if (this.feeForCancellation) {
                return 'feeForCancellation'
            } else {
                return 'special'
            }
        }

        get labelColor() {
            switch (this.status) {
                case 'isLoading':
                    return 'text-black text-medium-emphasis'
                case 'hasErrors':
                    return 'text-black text-medium-emphasis'
                case 'isNonRefundable':
                    return 'text-error'
                case 'isFree':
                case 'freeDate':
                    return 'text-success'
                case 'feeForCancellation':
                    return 'text-warning'
                default:
                    return 'text-black text-medium-emphasis'
            }
        }

        get label() {
            switch (this.status) {
                case 'isLoading':
                case 'hasErrors':
                    return this.$t('cancellation_policy')
                case 'isNonRefundable':
                    return this.$t('non_refundable')
                case 'isFree':
                    return this.isFreeLabel
                case 'freeDate':
                    return this.$t('free_cancellation_before_date', {
                        date: this.$filters.dateFormat(this.freeDate),
                    })
                case 'feeForCancellation':
                    return this.$t('fee_for_cancellation')
                default:
                    return this.$t('cancellation_policy')
            }
        }

        get isFreeLabel() {
            return this.$t('cancellation_free')
        }

        get showPrice() {
            return this.price.amount !== undefined
        }
    }

    export default ConditionsLabel
</script>

<style scoped lang="scss">
    a {
        cursor: pointer;
    }
</style>
